import axios from 'axios'
import {sanitizePayload} from '../utils/apiUtils'
import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

// WC Code interface for Bill Code creation
export interface WCCodeForBillCode {
  id: string
  state: string
  wcCode: string
  description: string
  rate?: number
  effectiveDate?: string
  expirationDate?: string
}

// Types and Interfaces
export interface Agency {
  id: string
  _id?: string
  name: string
  email?: string // Optional in new schema
  phoneNumber: string
  phoneExtension?: string // Changed from phoneNumberExt
  address: Address
  billingAddress: Address // New field
  website?: string // Changed from agencyWebsite
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  isMsp: boolean
  isEor: boolean // New field
  parentAgencyId?: string // New field for hierarchy
  wcCatalogIds?: string[] // NEW: List of assigned WC Catalog IDs
  status: EntityStatus
  createdDate?: string
  modifiedDate?: string
  // EOR Profile Fields
  eorLegalName?: string
  dbaName?: string
  eorMainOfficeAddress?: Address
  eorBillingAddress?: Address
  eorPhoneNumber?: string
  eorType?: EorType
  eorInternalCode?: string
  entityStartDate?: string
  businessLicenseNumber?: string
  industryCode?: string
  eorFaxNumber?: string
  // Sub-profiles
  taxCompliance?: TaxCompliance
  banking?: Banking
  /** Up to 4 uppercase alphanumeric characters prepended to EOR invoice numbers. */
  invoicePrefix?: string
}

export interface Address {
  line1: string
  city: string
  state: string
  zipCode: string
  display?: string
}

// Enhanced CreateAgencyRequest to match backend
export interface CreateAgencyRequest {
  name: string
  phoneNumber: string
  phoneExtension?: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  address: Address
  billingAddress: Address
  website?: string
  isMsp: boolean
  isEor: boolean
  parentAgencyId?: string
  markupPercentage?: number // New field for branch agencies
}

// EOR Profile Types
export enum EorType {
  Corporation = 'Corporation',
  LLC = 'LLC',
  Partnership = 'Partnership',
  NotSpecified = 'Not Specified',
}

export enum EorBankingAccountType {
  Checking = 'Checking',
  Savings = 'Savings',
}

export enum EorFileDeliveryMethod {
  Email = 'Email',
  SFTP = 'SFTP',
  API = 'API',
}

export enum EorFilingFrequency {
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annual = 'Annual',
}

// EOR Sub-Profile Interfaces
export interface TaxCompliance {
  federalEin?: string
  irsFilingFrequency?: EorFilingFrequency
  stateWithholdingTaxId?: string
  sutaRate?: number
  localTaxIds?: string
  workersCompPolicyNumber?: string
  workersCompCarrier?: string
  disabilityInsuranceReg?: string
  stateUiAccounts?: StateUiAccount[]
}

export type StateRegistrationStatus = 'active' | 'pending' | 'needs_attention'

export interface StateUiAccount {
  state: string
  accountNumber?: string
  suiRate?: number
  ettRate?: number
  status?: StateRegistrationStatus
  effectiveDate?: string | null
  taxableWageBase?: number
  rateYear?: string
  /** @deprecated Legacy field — use suiRate */
  rate?: number
  ratePercent?: number
}

export interface Banking {
  bankName?: string
  routingNumber?: string
  accountType?: EorBankingAccountType
  bankContactPerson?: string
  achCompanyName?: string
  achCompanyId?: string
  achOriginatorId?: string
  achFileNamePrefix?: string
  achFileDeliveryMethod?: EorFileDeliveryMethod
  positivePayActivation?: boolean
}

// EOR Profile DTOs - aligned with swagger UpdateEorProfileRequest
export interface UpdateEorProfileRequest {
  dbaName?: string
  eorLegalName?: string
  eorPhoneNumber?: string
  eorMainOfficeAddress?: Address
  eorBillingAddress?: Address
  eorType: EorType
  eorInternalCode: string
  federalEin: string // Required per swagger
  entityStartDate?: Date
  businessLicenseNumber?: string
  industryCode?: string
  eorFaxNumber?: string
}

export interface UpdateTaxComplianceRequest {
  federalEin?: string
  irsFilingFrequency?: EorFilingFrequency
  stateWithholdingTaxId?: string
  sutaRate?: number
  localTaxIds?: string
  workersCompPolicyNumber?: string
  workersCompCarrier?: string
  disabilityInsuranceReg?: string
  paidFamilyLeaveReg?: string
  stateUiAccounts?: StateUiAccount[]
}

export interface UpdateBankingRequest {
  bankName?: string
  bankRoutingNumber?: string
  bankAccountNumber?: string
  bankAccountType?: EorBankingAccountType
  bankContactPerson?: string
  achCompanyName?: string
  achCompanyId?: string
  achOriginatorId?: string
  achFileNamePrefix?: string
  achFileDeliveryMethod?: EorFileDeliveryMethod
  positivePayActivation?: boolean
}

// Response DTOs
export type AgencyProfileResponse = Agency

export interface AgencyHierarchyResponse {
  agency: AgencyProfileResponse
  branchs: AgencyProfileResponse[] // Note: backend uses "branchs" not "branches"
}

export interface AgencyAffiliates {
  agencyId: string
  markupPercentage: number
}

export interface AgencyAffiliateResponse {
  id: string
  name: string
  markup: number
}

export interface EorProfileResponse {
  id: string
  versionKey: number
  createdDate: string
  modifiedDate: string
  createdBy?: string
  modifiedBy?: string
  eorLegalName: string
  eorPhoneNumber: string
  eorMainOfficeAddress: Address
  eorBillingAddress: Address
  dbaName?: string
  eorType?: EorType
  eorInternalCode?: string
  federalEin?: string
  entityStartDate?: Date
  businessLicenseNumber?: string
  industryCode?: string
  eorFaxNumber?: string
}

export interface TaxComplianceProfileResponse {
  federalEin?: string
  irsFilingFrequency?: EorFilingFrequency
  stateWithholdingTaxId?: string
  sutaRate?: number
  localTaxIds?: string
  workersCompPolicyNumber?: string
  workersCompCarrier?: string
  disabilityInsuranceReg?: string
  paidFamilyLeaveReg?: string
  stateUiAccounts?: StateUiAccount[]
}

export interface BankingProfileResponse {
  bankName?: string
  bankRoutingNumber?: string
  bankAccountNumber?: string
  bankAccountType?: EorBankingAccountType
  bankContactPerson?: string
  achCompanyName?: string
  achCompanyId?: string
  achOriginatorId?: string
  achFileNamePrefix?: string
  achFileDeliveryMethod?: EorFileDeliveryMethod
  positivePayActivation: boolean
  eorDocumentDetails?: {
    documentId: string
    folderName: string
    file?: {
      name: string
      url?: string
    }
  }
}

export interface UpdateAgencyRequest {
  id: string
  name?: string
  email?: string
  phoneNumber?: string
  phoneNumberExt?: number
  address?: Address
  agencyWebsite?: string
  adminFirstName?: string
  adminLastName?: string
  adminEmail?: string
  isMsp?: boolean
  /** Up to 4 uppercase alphanumeric characters prepended to EOR invoice numbers (e.g. "ACME"). */
  invoicePrefix?: string
}

export interface UpdateAgencyStatusRequest {
  status: EntityStatus
}

export interface AgencyNamesAndIds {
  id: string
  name: string
}

export interface EorInfo {
  id: string
  name: string
  location: {
    line1: string
    city: string
    state: string
    zipCode: string
    display: string
    location?: {
      type: string
      coordinates: (number | null)[]
    }
  }
  status: EntityStatus
  numberOfSites: number
  federalEin: string
  /**
   * STFFLG-3596 / SHR-3848: open-batch indicator counts. Optional because
   * /agency/getAllEorsInfo does not populate them yet — the authoritative source
   * is GET /payroll/eors (see EORItem). Kept here so PayrollPage can carry them
   * through to EorCard once the endpoint returns them without another type change.
   */
  pendingApprovalCount?: number
  readyToProcessCount?: number
}

export interface AgencyPagingModel {
  agencies: Agency[]
  totalAgencies: number
}

export interface PagingParametersDto {
  pageNumber?: number
  pageSize?: number
  search?: string
}

export interface GetAgenciesQueryDto {
  pageNumber?: number
  pageSize?: number
  status?: EntityStatus
  name?: string
  adminEmail?: string
  createdDate?: string
}

export enum EntityStatus {
  Pending = 'Pending',
  Active = 'Active',
  Disabled = 'Disabled',
  Creating = 'Creating',
  Failed = 'Failed',
  Archived = 'Archived',
}

/**
 * Agency API service for MSP NodeJS integration
 * Handles all agency-related operations
 */
export class AgencyApi extends BaseApi {
  /**
   * Get agency names by IDs
   * POST /agency/getAgencyNamesByIds
   */
  async getAgencyNamesByIds(ids: string[]): Promise<AgencyNamesAndIds[]> {
    try {
      const response = await this.client.post<AgencyNamesAndIds[]>(
        '/agency/getAgencyNamesByIds',
        ids,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency names by IDs:', error)
      throw error
    }
  }

  /**
   * Get agency MSP status by ID
   * GET /agency/getAgencyMspStatusById/:id
   */
  async getAgencyMspStatusById(id: string): Promise<boolean> {
    try {
      const response = await this.client.get<boolean>(`/agency/getAgencyMspStatusById/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency MSP status: ${id}`, error)
      throw error
    }
  }

  /**
   * Get agencies by MSP status
   * GET /agency/getAgenciesByMspStatus/:isMsp
   */
  async getAgenciesByMspStatus(isMsp: boolean): Promise<Agency[]> {
    try {
      const response = await this.client.get<Agency[]>(`/agency/getAgenciesByMspStatus/${isMsp}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agencies by MSP status:', error)
      throw error
    }
  }

  /**
   * Get paginated agencies with optional filters
   * GET /agency/getAgencies
   */
  async getAgencies(query?: GetAgenciesQueryDto): Promise<AgencyPagingModel> {
    try {
      const response = await this.client.get<AgencyPagingModel>('/agency/getAgencies', {
        params: query,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agencies:', error)
      throw error
    }
  }

  /**
   * Get all agencies
   * GET /agency/getAllAgencies
   */
  async getAllAgencies(): Promise<Agency[]> {
    try {
      const response = await this.client.get<Agency[]>('/agency/getAllAgencies')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching all agencies:', error)
      throw error
    }
  }

  /**
   * Get agency names
   * GET /agency/getAgencyNames/Names
   */
  async getAgencyNames(): Promise<string[]> {
    try {
      const response = await this.client.get<string[]>('/agency/getAgencyNames/Names')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching agency names:', error)
      throw error
    }
  }

  /**
   * Find agency by name
   * GET /agency/findAgencyName/find/:name
   */
  async findAgencyByName(name: string): Promise<Agency | null> {
    try {
      const response = await this.client.get<Agency | null>(`/agency/findAgencyName/find/${name}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error finding agency by name:', error)
      throw error
    }
  }

  /**
   * Get agency profile by ID (enhanced with EOR data)
   * GET /agency/getAgency/:id
   */
  async getAgencyById(id: string): Promise<AgencyProfileResponse | null> {
    try {
      const response = await this.client.get<AgencyProfileResponse | null>(
        `/agency/getAgency/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency: ${id}`, error)
      throw error
    }
  }

  /**
   * Create new agency
   * POST /agency/onboardAgency
   */
  async createAgency(agencyData: CreateAgencyRequest): Promise<Agency> {
    try {
      const sanitizedData = sanitizePayload(agencyData)
      const response = await this.client.post<Agency>('/agency/onboardAgency', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating agency:', error)
      throw error
    }
  }

  /**
   * Delete agency
   * DELETE /agency/deleteAgency
   */
  async deleteAgency(id: string): Promise<Agency> {
    try {
      const response = await this.delete<Agency>(`/agency/deleteAgency?id=${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error deleting agency: ${id}`, error)
      throw error
    }
  }

  /**
   * Update agency
   * PUT /agency/updateAgency/{id}
   */
  async updateAgency(agencyData: UpdateAgencyRequest): Promise<Agency> {
    try {
      const sanitizedData = sanitizePayload(agencyData)
      // ID is passed as URL parameter, not in body
      const response = await this.client.put<Agency>(
        `/agency/updateAgency/${agencyData.id}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating agency: ${agencyData.id}`, error)
      throw error
    }
  }

  /**
   * Update agency status (archive/activate)
   * PUT /agency/updateAgencyStatus/:id/Status/:status
   */
  async updateAgencyStatus(id: string, status: EntityStatus): Promise<Agency> {
    try {
      const response = await this.client.put<Agency>(
        `/agency/updateAgencyStatus/${id}/Status/${status}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating agency status: ${id}`, error)
      throw error
    }
  }

  /**
   * Archive agency
   * PUT /agency/archiveAgency/:id
   */
  async archiveAgency(id: string): Promise<Agency> {
    try {
      const response = await this.client.put<Agency>(`/agency/archiveAgency/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error archiving agency: ${id}`, error)
      throw error
    }
  }

  /**
   * Restore archived agency
   * PUT /agency/restoreAgency/:id
   */
  async restoreAgency(id: string): Promise<Agency> {
    try {
      const response = await this.client.put<Agency>(`/agency/restoreAgency/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error restoring agency: ${id}`, error)
      throw error
    }
  }

  // ===============================
  // EOR PROFILE MANAGEMENT
  // ===============================

  /**
   * Get agency EOR profile
   * GET /agency/getAgencyEorProfile/:id
   */
  async getAgencyEorProfile(id: string): Promise<EorProfileResponse> {
    try {
      const response = await this.client.get<EorProfileResponse>(
        `/agency/getAgencyEorProfile/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency EOR profile: ${id}`, error)
      throw error
    }
  }

  /**
   * Update agency EOR profile
   * PUT /agency/updateAgencyEorProfile/:id
   */
  async updateAgencyEorProfile(id: string, profileData: UpdateEorProfileRequest): Promise<Agency> {
    try {
      const sanitizedData = sanitizePayload(profileData)
      const response = await this.client.put<Agency>(
        `/agency/updateAgencyEorProfile/${id}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating agency EOR profile: ${id}`, error)
      throw error
    }
  }

  /**
   * Get agency tax compliance profile
   * GET /agency/getAgencyTaxComplianceProfile/:id
   */
  async getAgencyTaxComplianceProfile(id: string): Promise<TaxComplianceProfileResponse> {
    try {
      const response = await this.client.get<TaxComplianceProfileResponse>(
        `/agency/getAgencyTaxComplianceProfile/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency tax compliance profile: ${id}`, error)
      throw error
    }
  }

  /**
   * Update agency tax compliance
   * PUT /agency/updateAgencyTaxCompliance/:id
   */
  async updateAgencyTaxCompliance(
    id: string,
    taxData: UpdateTaxComplianceRequest,
  ): Promise<Agency> {
    try {
      const sanitizedData = sanitizePayload(taxData)
      const response = await this.client.put<Agency>(
        `/agency/updateAgencyTaxCompliance/${id}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating agency tax compliance: ${id}`, error)
      throw error
    }
  }

  /**
   * Get agency banking profile
   * GET /agency/getAgencyBankingProfile/:id
   */
  async getAgencyBankingProfile(id: string): Promise<BankingProfileResponse> {
    try {
      const response = await this.client.get<BankingProfileResponse>(
        `/agency/getAgencyBankingProfile/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency banking profile: ${id}`, error)
      throw error
    }
  }

  /**
   * Update agency banking
   * PUT /agency/updateAgencyBanking/:id
   */
  async updateAgencyBanking(
    id: string,
    bankingData: UpdateBankingRequest,
  ): Promise<Agency & {eorDocumentDetails?: {documentId: string; folderName: string}}> {
    try {
      const sanitizedData = sanitizePayload(bankingData)
      const response = await this.client.put<
        Agency & {eorDocumentDetails?: {documentId: string; folderName: string}}
      >(`/agency/updateAgencyBanking/${id}`, sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error updating agency banking: ${id}`, error)
      throw error
    }
  }

  // ===============================
  // WC CATALOG ASSIGNMENT MANAGEMENT
  // ===============================

  /**
   * Assign WC Catalogs to Agency
   * POST /agency/assignWcCatalogs
   */
  async assignWcCatalogs(agencyId: string, catalogIds: string[]): Promise<unknown> {
    try {
      const response = await this.client.post('/agency/assignWcCatalog', {
        agencyId: agencyId,
        wcCatalogIds: catalogIds,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error assigning WC catalogs to agency: ${agencyId}`, error)
      throw error
    }
  }

  /**
   * Unassign WC Catalogs from Agency
   * POST /agency/unassignWcCatalog
   */
  async unassignWcCatalogs(agencyId: string, catalogIds: string[]): Promise<unknown> {
    try {
      const response = await this.client.post('/agency/unassignWcCatalog', {
        agencyId: agencyId,
        wcCatalogIds: catalogIds,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error unassigning WC catalogs from agency: ${agencyId}`, error)
      throw error
    }
  }

  /**
   * Get Assigned WC Catalogs for Agency
   * GET /agency/getAssignedWcCatalogs/:id
   */
  async getAssignedWcCatalogs(
    agencyId: string,
    filters?: {
      catalogName?: string
      insuranceCarrier?: string
      policyNumber?: string
      effectiveDate?: string
      expirationDate?: string
      status?: string[]
      pageNumber?: number
      pageSize?: number
      sortBy?: string
      sortDirection?: 'asc' | 'desc'
    },
  ): Promise<Record<string, unknown>[]> {
    try {
      const params = new URLSearchParams()

      // Always ensure status is passed - default to Active and Incomplete for agency view
      const statusToUse =
        filters?.status && filters.status.length > 0 ? filters.status : ['Active', 'Incomplete']

      // Add status parameters first
      statusToUse.forEach((status) => params.append('status', status))

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '' && key !== 'status') {
            params.append(key, String(value))
          }
        })
      }

      const queryString = params.toString()
      const url = `/agency/getAssignedWcCatalogs/${agencyId}${queryString ? `?${queryString}` : ''}`

      const response = await this.client.get<Record<string, unknown>[]>(url)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching assigned WC catalogs for agency: ${agencyId}`, error)
      throw error
    }
  }

  /**
   * Get Agencies by Catalog ID
   * GET /agency/getAgenciesByCatalogId/:catalogId
   */
  async getAgenciesByCatalogId(catalogId: string): Promise<Agency[]> {
    try {
      const response = await this.client.get<Agency[]>(
        `/agency/getAgenciesByCatalogId/${catalogId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agencies by catalog ID: ${catalogId}`, error)
      throw error
    }
  }

  // ===============================
  // NEW ENDPOINTS FROM LATEST COMMITS
  // ===============================

  /**
   * Get Agency Hierarchy (Parent + Branches)
   * GET /agency/getAgencyHierarchy/:id
   */
  async getAgencyHierarchy(id: string): Promise<AgencyHierarchyResponse> {
    try {
      const response = await this.client.get<AgencyHierarchyResponse>(
        `/agency/getAgencyHierarchy/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency hierarchy: ${id}`, error)
      throw error
    }
  }

  /**
   * Get All EOR Agencies
   * GET /agency/getAllEors
   */
  async getAllEors(): Promise<AgencyNamesAndIds[]> {
    try {
      const response = await this.client.get<AgencyNamesAndIds[]>('/agency/getAllEors')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching all EOR agencies:', error)
      throw error
    }
  }

  /**
   * Get All EOR Agencies with detailed info (location, sites, etc.)
   * GET /agency/getAllEorsInfo
   * @param config Optional config with AbortSignal for cancellation
   */
  async getAllEorsInfo(config?: {signal?: AbortSignal}): Promise<EorInfo[]> {
    try {
      const response = await this.client.get<EorInfo[]>('/agency/getAllEorsInfo', {
        signal: config?.signal,
        timeout: 60000, // 60 seconds - this endpoint can be slow
      })
      return response.data
    } catch (error: unknown) {
      // Don't log cancelled requests (from AbortController)
      if (axios.isCancel(error)) {
        throw error
      }
      reportCatchError('Error fetching all EOR agencies info:', error)
      throw error
    }
  }

  /**
   * Add Agency Affiliate
   * PUT /agency/addAgencyAffiliate/:id
   */
  async addAgencyAffiliate(id: string, affiliate: AgencyAffiliates): Promise<void> {
    try {
      const sanitizedData = sanitizePayload(affiliate)
      await this.client.put(`/agency/addAgencyAffiliate/${id}`, sanitizedData)
    } catch (error: unknown) {
      reportCatchError(`Error adding agency affiliate: ${id}`, error)
      throw error
    }
  }

  /**
   * Get Agency Affiliates
   * GET /agency/getAgencyAffiliates/:id
   */
  async getAgencyAffiliates(id: string): Promise<AgencyAffiliateResponse[]> {
    try {
      const response = await this.client.get<AgencyAffiliateResponse[]>(
        `/agency/getAgencyAffiliates/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching agency affiliates: ${id}`, error)
      throw error
    }
  }

  /**
   * Delete Agency Affiliate
   * DELETE /agency/deleteAgencyAffiliate/:agencyId/:affiliateId
   */
  async deleteAgencyAffiliate(agencyId: string, affiliateId: string): Promise<void> {
    try {
      await this.client.put(`/agency/deleteAgencyAffiliate/${agencyId}/${affiliateId}`)
    } catch (error: unknown) {
      reportCatchError(`Error deleting agency affiliate: ${agencyId}/${affiliateId}`, error)
      throw error
    }
  }

  /**
   * Get Managed Sites by EOR Agency (with pagination and search)
   * GET /agency/getManagedSites/:agencyId
   */
  async getManagedSites(
    agencyId: string,
    pageNumber: number = 1,
    pageSize: number = 10,
    siteName?: string,
  ): Promise<{sites: Record<string, unknown>[]; totalSites: number}> {
    try {
      const response = await this.client.get<{
        sites: Record<string, unknown>[]
        totalSites: number
      }>(`/agency/getManagedSites/${agencyId}`, {
        params: {
          pageNumber,
          pageSize,
          ...(siteName && {siteName}),
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching managed sites for agency: ${agencyId}`, error)
      throw error
    }
  }

  /**
   * Get WC Codes by Agency ID for Bill Code creation
   * GET /agency/getWcCodesByAgencyId/{id}
   */
  async getWcCodesByAgencyId(
    agencyId: string,
    status: string[] = ['Active', 'Inactive'],
  ): Promise<WCCodeForBillCode[]> {
    try {
      const params = new URLSearchParams()

      // Always pass status parameter - API requires it
      status.forEach((statusValue) => {
        params.append('status', statusValue)
      })

      const queryString = params.toString()
      const url = `/agency/getWcCodesByAgencyId/${agencyId}${queryString ? `?${queryString}` : ''}`

      const response = await this.client.get<WCCodeForBillCode[]>(url)
      return response.data
    } catch (error: unknown) {
      reportCatchError(`Error fetching WC codes for agency: ${agencyId}`, error)
      throw error
    }
  }
}

export const agencyApi = new AgencyApi()
